/**
 * Automatic Error Recovery System
 * Provides comprehensive error detection, recovery, and prevention
 * Production-ready with intelligent recovery strategies
 */

class AutomaticErrorRecovery {
  constructor() {
    this.errorHistory = [];
    this.recoveryStrategies = new Map();
    this.recoveryAttempts = new Map();
    this.maxRecoveryAttempts = 3;
    this.recoveryCooldown = 5000; // 5 seconds between recovery attempts
    this.lastRecoveryTime = 0;
    this._handlingConsoleError = false;
  }

  // Initialize error recovery system
  initialize() {
    console.log('[AutomaticErrorRecovery] Initializing error recovery system...');

    this.setupGlobalErrorHandlers();
    this.setupNetworkErrorRecovery();
    this.setupResourceErrorRecovery();
    this.setupJavaScriptErrorRecovery();
    this.setupPerformanceErrorRecovery();
    this.setupStorageErrorRecovery();

    // Setup periodic health checks
    this.setupHealthChecks();

    console.log('[AutomaticErrorRecovery] Error recovery system initialized');
  }

  // Setup global error handlers
  setupGlobalErrorHandlers() {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('[AutomaticErrorRecovery] Unhandled promise rejection:', event.reason);
      this.handleError('promise_rejection', event.reason, event);
      event.preventDefault(); // Prevent default browser handling
    });

    // Handle uncaught errors
    window.addEventListener('error', (event) => {
      console.error('[AutomaticErrorRecovery] Uncaught error:', event.error);
      this.handleError('uncaught_error', event.error, event);
    });

    // NOTE: We intentionally do NOT override console.error. There is no
    // 'console_error' recovery strategy, so routing every logged error through
    // handleError only added string-matching + bookkeeping overhead on the app's
    // hundreds of console.error call sites while recovering nothing.
  }

  // Setup network error recovery
  setupNetworkErrorRecovery() {
    this.recoveryStrategies.set('network_error', [
      this.retryRequest.bind(this),
      this.useOfflineCache.bind(this),
      this.showOfflineMessage.bind(this)
    ]);

    this.recoveryStrategies.set('api_error', [
      this.retryApiCall.bind(this),
      this.useStaleData.bind(this),
      this.showRetryMessage.bind(this)
    ]);
  }

  // Setup resource error recovery
  setupResourceErrorRecovery() {
    // Handle failed resource loading
    window.addEventListener('error', (event) => {
      if (event.target && (event.target.tagName === 'IMG' || event.target.tagName === 'SCRIPT' || event.target.tagName === 'LINK')) {
        this.handleResourceError(event.target);
      }
    }, true);

    this.recoveryStrategies.set('resource_error', [
      this.retryResourceLoad.bind(this),
      this.useFallbackResource.bind(this),
      this.removeFailedResource.bind(this)
    ]);
  }

  // Setup JavaScript error recovery
  setupJavaScriptErrorRecovery() {
    this.recoveryStrategies.set('javascript_error', [
      this.reloadComponent.bind(this),
      this.resetComponentState.bind(this),
      this.showErrorFallback.bind(this)
    ]);

    this.recoveryStrategies.set('component_error', [
      this.recreateComponent.bind(this),
      this.useErrorBoundary.bind(this),
      this.showComponentError.bind(this)
    ]);
  }

  // Setup performance error recovery
  setupPerformanceErrorRecovery() {
    this.recoveryStrategies.set('performance_error', [
      this.optimizePerformance.bind(this),
      this.reduceLoad.bind(this),
      this.showPerformanceWarning.bind(this)
    ]);

    // Monitor for performance issues
    if ('performance' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'measure' && entry.duration > 5000) { // 5 seconds
            this.handleError('performance_error', {
              message: 'Performance issue detected',
              duration: entry.duration,
              name: entry.name
            });
          }
        }
      });

      observer.observe({ entryTypes: ['measure'] });
    }
  }

  // Setup storage error recovery
  setupStorageErrorRecovery() {
    this.recoveryStrategies.set('storage_error', [
      this.repairStorage.bind(this),
      this.clearCorruptedData.bind(this),
      this.useMemoryFallback.bind(this)
    ]);
  }

  // Setup periodic health checks
  setupHealthChecks() {
    setInterval(() => {
      this.performHealthCheck();
    }, 30000); // Every 30 seconds
  }

  // Main error handling function
  async handleError(type, error, event = null) {
    try {
      console.log(`[AutomaticErrorRecovery] Handling ${type} error:`, error);

      // Record error in history
      this.errorHistory.push({
        type: type || 'unknown',
        error: error || 'Unknown error',
        timestamp: new Date().toISOString(),
        userAgent: navigator ? navigator.userAgent : 'Unknown',
        url: window.location ? window.location.href : 'Unknown'
      });

      // Limit error history to last 100 errors
      if (this.errorHistory.length > 100) {
        this.errorHistory = this.errorHistory.slice(-100);
      }

    // Check if we're in recovery cooldown
    const now = Date.now();
    if (now - this.lastRecoveryTime < this.recoveryCooldown) {
      console.log('[AutomaticErrorRecovery] In recovery cooldown, skipping recovery attempt');
      return;
    }

    // Get recovery attempts for this error type
    const attempts = this.recoveryAttempts.get(type) || 0;

    if (attempts >= this.maxRecoveryAttempts) {
      console.warn(`[AutomaticErrorRecovery] Max recovery attempts reached for ${type}`);
      this.showMaxAttemptsMessage(type);
      return;
    }

    // Attempt recovery
    const recoveryStrategies = this.recoveryStrategies.get(type);
    if (recoveryStrategies) {
      for (const strategy of recoveryStrategies) {
        try {
          const result = await strategy(error, event);
          if (result.success) {
            console.log(`[AutomaticErrorRecovery] Successfully recovered from ${type} using ${strategy.name}`);
            this.recoveryAttempts.set(type, 0); // Reset attempts on success
            this.lastRecoveryTime = now;
            return;
          }
        } catch (strategyError) {
          console.warn(`[AutomaticErrorRecovery] Recovery strategy ${strategy.name} failed:`, strategyError);
        }
      }
    }

    // If all strategies failed, increment attempts
    this.recoveryAttempts.set(type, attempts + 1);

      // Show user-friendly error message
      this.showErrorMessage(type, error);
    } catch (handlerError) {
      // Prevent error handler from causing more errors
      console.warn('[AutomaticErrorRecovery] Error in error handler:', handlerError);
    }
  }

  // Handle console errors
  handleConsoleError(args) {
    try {
      // Extract meaningful error information
      const errorMessage = args ? args.join(' ') : 'Unknown console error';
      if (errorMessage && (errorMessage.includes('Error') || errorMessage.includes('Exception') || errorMessage.includes('TypeError') || errorMessage.includes('ReferenceError'))) {
        this.handleError('console_error', { message: errorMessage });
      }
    } catch (error) {
      // Prevent infinite error loops
      console.warn('[AutomaticErrorRecovery] Error in console error handler:', error);
    }
  }

  // Handle resource loading errors
  async handleResourceError(resource) {
    const resourceType = resource.tagName.toLowerCase();
    const resourceUrl = resource.src || resource.href;

    console.warn(`[AutomaticErrorRecovery] Resource failed to load: ${resourceType} - ${resourceUrl}`);

    await this.handleError('resource_error', {
      type: resourceType,
      url: resourceUrl,
      resource
    });
  }

  // Recovery strategies implementation

  // Retry request
  async retryRequest(error, event) {
    if (event && event.target && typeof event.target.retry === 'function') {
      console.log('[AutomaticErrorRecovery] Retrying request...');
      event.target.retry();
      return { success: true };
    }
    return { success: false };
  }

  // Use offline cache
  async useOfflineCache(error, event) {
    if ('caches' in window) {
      try {
        const cache = await caches.open('offline-cache');
        const cachedResponse = await cache.match(event.target.src || event.target.href);

        if (cachedResponse) {
          console.log('[AutomaticErrorRecovery] Using cached resource');
          event.target.src = URL.createObjectURL(await cachedResponse.blob());
          return { success: true };
        }
      } catch (cacheError) {
        console.warn('[AutomaticErrorRecovery] Cache fallback failed:', cacheError);
      }
    }
    return { success: false };
  }

  // Show offline message
  showOfflineMessage() {
    this.showNotification('You appear to be offline. Some features may not be available.', 'warning');
    return { success: true };
  }

  // Retry API call
  async retryApiCall(error, event) {
    // Implement exponential backoff retry logic
    const retryDelays = [1000, 2000, 4000]; // 1s, 2s, 4s
    const attempt = this.recoveryAttempts.get('api_error') || 0;

    if (attempt < retryDelays.length) {
      console.log(`[AutomaticErrorRecovery] Retrying API call in ${retryDelays[attempt]}ms...`);

      return new Promise((resolve) => {
        setTimeout(() => {
          // Retry the original request
          if (event && event.target && typeof event.target.send === 'function') {
            event.target.send();
            resolve({ success: true });
          } else {
            resolve({ success: false });
          }
        }, retryDelays[attempt]);
      });
    }

    return { success: false };
  }

  // Use stale data
  async useStaleData(error, event) {
    // Try to use cached/stale data if available
    if (window.appMemoryCache && window.appMemoryCache.has('stale_data')) {
      console.log('[AutomaticErrorRecovery] Using stale data fallback');
      const staleData = window.appMemoryCache.get('stale_data');
      // Apply stale data to the component/page (placeholder for actual implementation)
      console.log('[AutomaticErrorRecovery] Stale data available:', !!staleData);
      return { success: true };
    }
    return { success: false };
  }

  // Show retry message
  showRetryMessage() {
    this.showNotification('Connection issue detected. Retrying...', 'info');
    return { success: true };
  }

  // Retry resource load
  async retryResourceLoad(error, event) {
    const resource = error.resource;
    if (resource && resource.src) {
      console.log('[AutomaticErrorRecovery] Retrying resource load...');

      // Add cache buster to URL
      const separator = resource.src.includes('?') ? '&' : '?';
      resource.src = resource.src + separator + 'retry=' + Date.now();

      return { success: true };
    }
    return { success: false };
  }

  // Use fallback resource
  async useFallbackResource(error, event) {
    const resource = error.resource;
    const resourceType = error.type;

    if (resourceType === 'img') {
      // Use placeholder image
      resource.src = '/static/images/placeholder.png';
      console.log('[AutomaticErrorRecovery] Using fallback image');
      return { success: true };
    } else if (resourceType === 'script') {
      // Try to load from CDN fallback
      console.log('[AutomaticErrorRecovery] Script fallback not implemented');
      return { success: false };
    }

    return { success: false };
  }

  // Remove failed resource
  removeFailedResource(error, event) {
    const resource = error.resource;
    if (resource && resource.parentNode) {
      resource.parentNode.removeChild(resource);
      console.log('[AutomaticErrorRecovery] Removed failed resource');
      return { success: true };
    }
    return { success: false };
  }

  // Reload component
  async reloadComponent(error, event) {
    // Guard against reload loops within 60 seconds
    try {
      const now = Date.now();
      const last = Number(localStorage.getItem('__last_auto_reload_ts__') || '0');
      const count = Number(localStorage.getItem('__auto_reload_count__') || '0');
      if (now - last < 60000 && count >= 2) {
        console.warn('[AutomaticErrorRecovery] Skipping auto reload to avoid loop');
        return { success: false };
      }
      localStorage.setItem('__last_auto_reload_ts__', String(now));
      localStorage.setItem('__auto_reload_count__', String(now - last < 60000 ? count + 1 : 1));
    } catch {}

    // Trigger component reload if possible
    if (window.location) {
      console.log('[AutomaticErrorRecovery] Reloading component...');
      window.location.reload();
      return { success: true };
    }
    return { success: false };
  }

  // Reset component state
  resetComponentState(error, event) {
    // Reset application state
    if (window.appState) {
      window.appState = {};
      console.log('[AutomaticErrorRecovery] Reset component state');
      return { success: true };
    }
    return { success: false };
  }

  // Show error fallback
  showErrorFallback(error, event) {
    this.showNotification('An error occurred. The page has been refreshed to fix the issue.', 'warning');
    // Use the same loop guard as reloadComponent
    setTimeout(() => {
      try {
        const now = Date.now();
        const last = Number(localStorage.getItem('__last_auto_reload_ts__') || '0');
        const count = Number(localStorage.getItem('__auto_reload_count__') || '0');
        if (now - last < 60000 && count >= 2) {
          console.warn('[AutomaticErrorRecovery] Skipping auto reload (fallback) to avoid loop');
          return;
        }
        localStorage.setItem('__last_auto_reload_ts__', String(now));
        localStorage.setItem('__auto_reload_count__', String(now - last < 60000 ? count + 1 : 1));
      } catch {}
      window.location.reload();
    }, 2000);
    return { success: true };
  }

  // Recreate component
  recreateComponent(error, event) {
    // Force component recreation
    if (event && event.target && event.target.forceUpdate) {
      event.target.forceUpdate();
      console.log('[AutomaticErrorRecovery] Recreated component');
      return { success: true };
    }
    return { success: false };
  }

  // Use error boundary
  useErrorBoundary(error, event) {
    // Trigger error boundary if available
    console.log('[AutomaticErrorRecovery] Error boundary triggered');
    return { success: true };
  }

  // Show component error
  showComponentError(error, event) {
    this.showNotification('Component error detected. Please refresh the page.', 'error');
    return { success: true };
  }

  // Optimize performance
  async optimizePerformance(error, event) {
    // Trigger performance optimizations
    if (window.loadingSpeedOptimizer) {
      await window.loadingSpeedOptimizer.invalidateAllCaches();
      console.log('[AutomaticErrorRecovery] Performance optimized');
      return { success: true };
    }
    return { success: false };
  }

  // Reduce load
  reduceLoad(error, event) {
    // Reduce page load by disabling non-essential features
    document.body.classList.add('reduced-load');
    console.log('[AutomaticErrorRecovery] Load reduced');
    return { success: true };
  }

  // Show performance warning
  showPerformanceWarning() {
    this.showNotification('Performance issue detected. Some features may be slower.', 'warning');
    return { success: true };
  }

  // Repair storage
  async repairStorage(error, event) {
    if (window.browserMaintenance) {
      await window.browserMaintenance.recoverFromStorageCorruption();
      console.log('[AutomaticErrorRecovery] Storage repaired');
      return { success: true };
    }
    return { success: false };
  }

  // Clear corrupted data
  async clearCorruptedData(error, event) {
    if (window.browserMaintenance) {
      await window.browserMaintenance.emergencyStorageReset();
      console.log('[AutomaticErrorRecovery] Corrupted data cleared');
      return { success: true };
    }
    return { success: false };
  }

  // Use memory fallback
  useMemoryFallback(error, event) {
    // Switch to memory-only mode
    if (!window.memoryFallbackMode) {
      window.memoryFallbackMode = true;
      console.log('[AutomaticErrorRecovery] Switched to memory fallback mode');
      return { success: true };
    }
    return { success: false };
  }

  // Perform health check
  async performHealthCheck() {
    const healthStatus = {
      timestamp: new Date().toISOString(),
      checks: {}
    };

    // Check network connectivity. fetch ignores a `timeout` option, so use an
    // AbortController to actually cancel a hung request (otherwise every 30s
    // health check could leave a dangling request accumulating).
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch('/api/health', {
          method: 'HEAD',
          signal: controller.signal
        });
        healthStatus.checks.network = response.ok;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      healthStatus.checks.network = false;
    }

    // Check memory usage
    if (performance.memory) {
      const memoryUsage = (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100;
      healthStatus.checks.memory = memoryUsage < 80; // Healthy if under 80%
      healthStatus.memoryUsage = memoryUsage;
    }

    // Check storage health
    try {
      localStorage.setItem('health_check', 'test');
      localStorage.removeItem('health_check');
      healthStatus.checks.storage = true;
    } catch (error) {
      healthStatus.checks.storage = false;
    }

    // Log health status
    console.log('[AutomaticErrorRecovery] Health check results:', healthStatus);

    // Trigger recovery if health checks fail
    const failedChecks = Object.entries(healthStatus.checks)
      .filter(([key, healthy]) => !healthy)
      .map(([key]) => key);

    if (failedChecks.length > 0) {
      console.warn('[AutomaticErrorRecovery] Health check failed for:', failedChecks);
      this.handleError('health_check_failed', {
        failedChecks,
        healthStatus
      });
    }

    return healthStatus;
  }

  // Show notification
  showNotification(message, type = 'info') {
    // Map notification types to valid console methods
    const consoleMethod = {
      'info': 'log',
      'warning': 'warn',
      'warn': 'warn',
      'error': 'error',
      'success': 'log',
      'debug': 'log'
    }[type] || 'log';

    // Use toast notifications if available
    if (window.toast && typeof window.toast[type] === 'function') {
      window.toast[type](message);
    } else {
      // Fallback to console with proper method
      console[consoleMethod](`[${type.toUpperCase()}] ${message}`);
      if (type === 'error') {
        // Only show alert for actual errors to avoid spam
        console.error('Error details:', message);
      }
    }
  }

  // Show max attempts message
  showMaxAttemptsMessage(errorType) {
    try {
      const messages = {
        network_error: 'Network issues persist. Please check your connection.',
        api_error: 'Server communication issues. Please try again later.',
        javascript_error: 'Application error detected. Please refresh the page.',
        storage_error: 'Data storage issues detected. Please clear browser data.',
        performance_error: 'Performance issues detected. Please restart your browser.'
      };

      const message = messages[errorType] || 'Multiple errors detected. Please refresh the page.';
      this.showNotification(message, 'error');
    } catch (notificationError) {
      // Fallback to basic console logging if notification fails
      console.error('[AutomaticErrorRecovery] Could not show max attempts message:', notificationError);
    }
  }

  // Show user-friendly error message
  showErrorMessage(errorType, error) {
    try {
      const messages = {
        network_error: 'Connection lost. Attempting to reconnect...',
        api_error: 'Server error occurred. Retrying request...',
        javascript_error: 'Application error detected. Attempting recovery...',
        resource_error: 'Resource loading failed. Using fallback...',
        storage_error: 'Data storage issue detected. Attempting repair...',
        performance_error: 'Performance issue detected. Optimizing...'
      };

      const message = messages[errorType] || 'An error occurred. Attempting automatic recovery...';
      this.showNotification(message, 'warning');
    } catch (notificationError) {
      // Fallback to basic console logging if notification fails
      console.warn('[AutomaticErrorRecovery] Could not show error message:', notificationError);
    }
  }

  // Get recovery statistics
  getRecoveryStats() {
    return {
      totalErrors: this.errorHistory.length,
      recoveryAttempts: Object.fromEntries(this.recoveryAttempts),
      recentErrors: this.errorHistory.slice(-10),
      recoveryStrategies: Array.from(this.recoveryStrategies.keys())
    };
  }

  // Export error report
  exportErrorReport() {
    return {
      timestamp: new Date().toISOString(),
      errorHistory: this.errorHistory,
      recoveryAttempts: Object.fromEntries(this.recoveryAttempts),
      recoveryStats: this.getRecoveryStats(),
      browserInfo: {
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      }
    };
  }

  // Reset recovery state
  resetRecoveryState() {
    this.recoveryAttempts.clear();
    this.errorHistory = [];
    this.lastRecoveryTime = 0;
    console.log('[AutomaticErrorRecovery] Recovery state reset');
  }
}

// Create singleton instance
const automaticErrorRecovery = new AutomaticErrorRecovery();

// Auto-initialize when imported
if (typeof window !== 'undefined') {
  // Initialize after other systems are ready
  setTimeout(() => {
    automaticErrorRecovery.initialize();
  }, 500);
}

export default automaticErrorRecovery;
